import i18next from 'i18next'
import { Set } from 'immutable'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { Link } from 'widgets-for-react'
import { categoryIconId } from '../../../utils/categories'
import { DataLayer } from '../../../utils/datalayer'
import { formatDate } from '../../../utils/date'
import { blockInviewHandler } from '../../../utils/inview'
import { SVGIcon } from '../../../utils/svgicon'
import { RootLayout } from '../rootLayout'
import { ViewListenerState } from '../viewListener/viewListenerState'
import { Month, SeasonalCalendarData, months } from './seasonalCalendarState'

type SeasonalCalendarLayoutProps = {
  seasonalCalendarData:
    | { data: SeasonalCalendarData; showLoaderAsWell: boolean }
    | 'loading'
    | 'error'
  expandedMainCategories: Set<string>
  updateExpandedMainCategories: (_: Set<string>) => void
  filterOnMonth?: Month
  updateFilterOnMonth: (_?: Month) => void
  view: ViewListenerState
}

export const SeasonalCalendarLayout = (props: SeasonalCalendarLayoutProps) => (
  <InView
    as="section"
    threshold={0}
    triggerOnce={true}
    onChange={(isInView, entry) => blockInviewHandler(isInView, entry.target)}
    className="calendar"
  >
    <div className="calendar__inner">
      <div className="calendar__header container--m gutter--m">
        <SeasonalCalendarLegendLayout />
        <div className="calendar__filter">
          <select
            className="form-select"
            value={props.filterOnMonth}
            onChange={(e) => {
              props.updateFilterOnMonth(
                e.target.value === ''
                  ? undefined
                  : (parseInt(e.target.value) as Month)
              )
              DataLayer.filterSeasonCalendar(
                formatDate.month(i18next, new Date(0, parseInt(e.target.value)))
              )
            }}
          >
            {[
              <option key="" value="">
                {i18next.t('seasonalCalendar:filterOnMonth')}
              </option>,
            ].concat(
              months.map((month) => (
                <option key={month} value={month}>
                  {formatDate.month(i18next, new Date(0, month))}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {props.seasonalCalendarData == 'loading' ? (
        <RootLayout.Loader hasWrapper showText />
      ) : props.seasonalCalendarData == 'error' ? (
        <RootLayout.LoadingFailed
          text={i18next.t('common:loadingFailed', {
            entity: i18next.t('seasonalCalendar:title').toLowerCase(),
          })}
        />
      ) : props.seasonalCalendarData.data.length === 0 ? (
        <RootLayout.EmptyState
          text={i18next.t('common:emptyState', {
            entity: i18next.t('common:item_plural'),
          })}
        />
      ) : (
        <>
          {props.seasonalCalendarData.data.map((mainCategoryData, index) => {
            const mainCategory = mainCategoryData.mainCategoryName
            const expanded = props.expandedMainCategories.includes(mainCategory)
            return (
              <SeasonalCalendarMainCategoryLayout
                key={`${mainCategoryData.mainCategoryName}-main`}
                index={`${index}`}
                data={mainCategoryData}
                expanded={expanded}
                toggleExpanded={() =>
                  props.updateExpandedMainCategories(
                    expanded
                      ? props.expandedMainCategories.remove(mainCategory)
                      : props.expandedMainCategories.add(mainCategory)
                  )
                }
                view={props.view}
              />
            )
          })}
          {props.seasonalCalendarData.showLoaderAsWell && (
            <RootLayout.Loader hasWrapper showText />
          )}
        </>
      )}
    </div>
  </InView>
)

type SeasonalCalendarMainCategoryLayoutProps = {
  index: string
  data: SeasonalCalendarData[number]
  expanded: boolean
  toggleExpanded: () => void
  view: ViewListenerState
}

const SeasonalCalendarMainCategoryLayout = (
  props: SeasonalCalendarMainCategoryLayoutProps
) => (
  <section
    className="calendar-block container--m gutter--m"
    key={`sc-${props.index}`}
  >
    <div className="calendar-block__inner">
      <header
        className={`calendar-block__header${props.expanded ? ' expanded' : ''}`}
      >
        <h2 className="calendar-block__title title--xs">
          {props.data.mainCategoryName}
        </h2>
        <svg
          className="calendar-block__icon"
          width="22px"
          height="22px"
          aria-hidden="true"
        >
          <use
            xlinkHref={`#icon--${categoryIconId(props.data.mainCategoryTitle)}`}
          />
        </svg>
        {
          <button
            className="calender-block__toggle"
            aria-expanded={props.expanded}
            aria-controls={`a11y-g-${props.index}`}
            onClick={props.toggleExpanded}
          >
            <span className="sr-text">{i18next.t('a11y:toggleBlock')}</span>
            <SVGIcon
              className="calendar-block__toggle-icon"
              size={10}
              iconId="chevron-down"
            />
          </button>
        }
      </header>

      <div
        id={`a11y-g-${props.index}`}
        className="calendar-block__content"
        aria-hidden={!props.expanded}
      >
        {props.data.subcategories.map((subcategory, index) => (
          <SeasonalCalendarSubCategoryLayout
            key={`${subcategory.subcategoryName}-sub`}
            index={`${props.index}-${index}`}
            data={subcategory}
            mainCategoryName={props.data.mainCategoryName}
          />
        ))}
      </div>
    </div>
  </section>
)

type SeasonalCalendarSubCategoryLayoutProps = {
  index: string
  data: SeasonalCalendarData[number]['subcategories'][number]
  mainCategoryName: string
}

const SeasonalCalendarSubCategoryLayout = (
  props: SeasonalCalendarSubCategoryLayoutProps
) => (
  <section className="calendar-group" key={`sc-${props.index}`}>
    <header className="calendar-row calendar-group__header">
      <h3 className="calendar-row__header calendar-group__title text--m">
        <span>{props.data.subcategoryName}</span>
      </h3>
      <div
        className="calendar-row__items calendar-group__legend text--xs-to-s"
        aria-hidden="true"
      >
        {months.map((month) => {
          const date = (
            monthFormat: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
          ) =>
            new Date(0, month).toLocaleDateString(i18next.language, {
              month: monthFormat,
            })
          return (
            <div
              key={month}
              className="calendar-row__item calendar-group__month"
            >
              <abbr title={date('long')}>{date('short')}</abbr>
            </div>
          )
        })}
      </div>
    </header>

    {props.data.entries.map((entryData, index) => (
      <SeasonalCalendarEntryLayout
        key={`${entryData.ingredient}-${index}`}
        data={entryData}
        index={`${props.index}-${index}`}
        mainCategory={props.mainCategoryName}
        subCategory={props.data.subcategoryName}
      />
    ))}
  </section>
)

type SeasonalCalendarEntryLayoutProps = {
  index: string
  data: {
    characteristic: string
    ingredient: string
    variant: string
    MonthlyAvailabilities: number[]
  }
  mainCategory: string
  subCategory: string
}

const SeasonalCalendarEntryLayout = (
  props: SeasonalCalendarEntryLayoutProps
) => {
  const { data, index, mainCategory, subCategory } = props
  const { ingredient, characteristic, variant, MonthlyAvailabilities } = data
  return (
    <div className="calendar-row calendar-item" key={`sc-${index}`}>
      <div className="calendar-row__header calendar-item__header">
        <p className="calendar-item__title text--xs-to-s">
          <Link
            className="link--inverted"
            to={`/${i18next.language}/search?term=${ingredient}`}
            onClick={(e) =>
              DataLayer.clickIngredientSeasonCalendar(
                mainCategory,
                subCategory,
                ingredient
              )
            }
          >
            {ingredient}
          </Link>
          {characteristic && `, ${characteristic}`}
          {variant && `, ${variant}`}
        </p>
      </div>

      <div className="calendar-row__items calendar-item__fieldset text--xs-to-s">
        {MonthlyAvailabilities.map((availability, index) => (
          <div
            key={`sc-${index}`}
            className={`calendar-row__item calendar-item__month result--${
              availability == 0 ? 'poor' : availability == 1 ? 'good' : 'best'
            }`}
            title={
              availability == 0
                ? i18next.t('seasonalCalendar:availabilityPoor')
                : availability == 1
                ? i18next.t('seasonalCalendar:availabilityGood')
                : i18next.t('seasonalCalendar:availabilityBest')
            }
          >
            <span className="calendar-item__result sr-text">
              {formatDate.month(i18next, new Date(0, index))}:{' '}
              {availability == 0
                ? i18next.t('seasonalCalendar:availabilityPoor')
                : availability == 1
                ? i18next.t('seasonalCalendar:availabilityGood')
                : i18next.t('seasonalCalendar:availabilityBest')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

const SeasonalCalendarLegendLayout = () => (
  <div className="calendar__legend text--xs-to-s" aria-hidden="true">
    <div className="calendar__legend-item">
      <span className="calendar__legend-text">
        {i18next.t('seasonalCalendar:availabilityPoor')}
      </span>
      <span className="calendar__legend-icon result--poor" />
    </div>
    <div className="calendar__legend-item">
      <span className="calendar__legend-text">
        {i18next.t('seasonalCalendar:availabilityGood')}
      </span>
      <span className="calendar__legend-icon result--good" />
    </div>
    <div className="calendar__legend-item">
      <span className="calendar__legend-text">
        {i18next.t('seasonalCalendar:availabilityBest')}
      </span>
      <span className="calendar__legend-icon result--best" />
    </div>
  </div>
)
